import React, { useState } from 'react';
import {
  Search,
  Bot,
  MessageSquare,
  GitBranch,
  Database,
  Play,
  Pause,
  RotateCcw,
  Filter,
} from 'lucide-react';
import { Agent, Model, ComponentTemplate } from '../../types';

interface ComponentLibraryProps {
  agents: Agent[];
  models: Model[];
  onComponentClick?: (nodeType: string, nodeData: any) => void;
}

const ComponentLibrary: React.FC<ComponentLibraryProps> = ({ agents, models, onComponentClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Built-in component templates
  const builtInComponents: ComponentTemplate[] = [
    {
      id: 'user-prompt',
      name: 'User Prompt',
      description: 'Prompts the user for input',
      type: 'user_prompt',
      category: 'input',
      icon: 'MessageSquare',
      defaultConfig: {
        step_type: 'user_prompt',
        input_message: 'Please provide your input:',
      },
      requiredFields: ['input_message'],
    },
    {
      id: 'conditional',
      name: 'Conditional',
      description: 'Branching logic based on conditions',
      type: 'conditional',
      category: 'control',
      icon: 'GitBranch',
      defaultConfig: {
        step_type: 'conditional',
        condition: { conditions: [] },
        on_true: { action: 'continue' },
        on_false: { action: 'exit' },
      },
      requiredFields: ['condition'],
    },
  ];

  const categories = [
    { id: 'all', name: 'All Components', icon: Filter },
    { id: 'input', name: 'Input', icon: MessageSquare },
    { id: 'processing', name: 'Processing', icon: Bot },
    { id: 'output', name: 'Output', icon: Database },
    { id: 'control', name: 'Control Flow', icon: GitBranch },
  ];

  // Filter components based on search and category
  const filteredBuiltIns = builtInComponents.filter((component) => {
    const matchesSearch = component.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      component.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || component.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredAgents = agents.filter((agent) => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.instructions.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || selectedCategory === 'processing';
    return matchesSearch && matchesCategory;
  });

  // Handle drag start for components
  const onDragStart = (event: React.DragEvent, nodeType: string, nodeData: any) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/nodedata', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  const getIconComponent = (iconName: string) => {
    const icons: Record<string, any> = {
      MessageSquare,
      Bot,
      Database,
      GitBranch,
      Play,
      Pause,
      RotateCcw,
    };
    return icons[iconName] || Bot;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-3">Components</h2>
        
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-1">
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <IconComponent className="w-3 h-3 mr-1" />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Component list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Built-in components */}
        {filteredBuiltIns.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Built-in Components</h3>
            <div className="space-y-2">
              {filteredBuiltIns.map((component) => {
                const IconComponent = getIconComponent(component.icon);
                const nodeData = {
                  label: component.name,
                  ...component.defaultConfig,
                };
                const handleClick = () => {
                  // Tutorial step 11 is advanced by TutorialOverlay via click trigger on lib-item-userprompt
                  if (onComponentClick) {
                    onComponentClick(component.type, nodeData);
                  }
                };
                return (
                  <div
                    key={component.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, component.type, nodeData)}
                    onClick={handleClick}
                    className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-move hover:border-primary-300 hover:bg-primary-50 transition-colors group"
                    data-tutorial={component.type === 'user_prompt' ? 'lib-item-userprompt' : undefined}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <IconComponent className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white group-hover:text-primary-900">
                          {component.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {component.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Agent components */}
        {filteredAgents.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Agents</h3>
            <div className="space-y-2">
              {filteredAgents.map((agent) => {
                const nodeData = {
                  label: agent.name,
                  agent,
                  agentId: agent.agent_id,
                };
                const handleClick = () => {
                  // Tutorial step 13 is advanced by TutorialOverlay via click trigger on lib-item-agent
                  if (onComponentClick) {
                    onComponentClick('agent', nodeData);
                  }
                };
                return (
                  <div
                    key={agent.agent_id}
                    draggable
                    onDragStart={(e) => onDragStart(e, 'agent', nodeData)}
                    onClick={handleClick}
                    className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-move hover:border-primary-300 hover:bg-primary-50 transition-colors group"
                    data-tutorial="lib-item-agent"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Bot className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white group-hover:text-primary-900">
                          {agent.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {agent.instructions}
                        </p>
                        {agent.output_type === 'model' && (
                          <div className="flex items-center mt-2 space-x-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                              Model Output
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No results */}
        {filteredBuiltIns.length === 0 && filteredAgents.length === 0 && (
          <div className="text-center py-8">
            <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No components found matching your search.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Drag components onto the canvas to build your workflow
        </p>
      </div>
    </div>
  );
};

export default ComponentLibrary;